import React from 'react';
import { Tag, Space, Table } from 'antd';
import {
  EnvironmentOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  SwapOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
// import Spinner from './Spinner';
import Spinner from "@src/components/spinner";
import { useQuery } from '@apollo/client';
import { getAuditTrailByTaskIdQuery } from '@src/util/query/mobileResponse.query';
import { TERMINATED_STATUS, TERMINATION_PENDING_STATUS } from '@src/pages/site/siteDetailPage/component/siteTerminationCheckModal';


const TaskDetailsView = ({
  taskData,
  isRecurring,
  cancelResponseLoading,
  stopUntilFurtherNoticeByTemplateIdLoading,
  handelCancelTask,
  handelStopTask,
  getPriorityColor,
  getStatusColor
}) => {

  // ============================================================================
  // HELPERS
  // ============================================================================
  const isLoading = cancelResponseLoading || stopUntilFurtherNoticeByTemplateIdLoading;
  const isTaskCancelled = taskData?.status?.toLowerCase() === "cancel";

  const { data: auditTrailData, loading: auditTrailLoading } = useQuery(getAuditTrailByTaskIdQuery, {
    variables: { taskId: taskData?._id },
    skip: !taskData?._id,
    fetchPolicy: 'network-only',
  });

  const auditTrail = auditTrailData?.getAuditTrailByTaskId?.response || [];

  // "Terminate Site Contract" terminates the whole site, not just this one template - so once the
  // site itself is Terminated/Termination Pending (via ANY task's button, the site detail page, or
  // the sidebar's Terminate Site action), every other task/template for that same site needs to
  // reflect that too, not just the one template whose own isTerminated flag happened to get set.
  // taskData.siteDetail is the site's full document (mobileServices.service.ts's getMobileResponse
  // lookup doesn't project it down), so its live metadata.sitestatus is already right here.
  const siteStatus = String(taskData?.siteDetail?.metadata?.sitestatus || "").trim();
  const isSiteTerminated = siteStatus === TERMINATED_STATUS || siteStatus === TERMINATION_PENDING_STATUS;
  const isTaskStopped = taskData?.templateDetail?.isTerminated || isSiteTerminated;
  const stoppedOnDate = taskData?.templateDetail?.stopDate || taskData?.siteDetail?.metadata?.contractenddate;

  const getAssigneeName = () => {
    if (taskData?.employeeDetail) {
      const { firstname, lastname } = taskData.employeeDetail;
      return `${firstname} ${lastname || ''}`.trim();
    }
    return taskData?.supplierDetail?.suppliername || 'Not Assigned';
  };

  const formatDateTime = (dateTime) => {
    return dateTime ? dayjs(dateTime).format('DD MMM YYYY · HH:mm') : 'Not Set';
  };

  const etaTableData = taskData?.eta?.map((etaItem, index) => ({
    key: index,
    eta: etaItem.eta,
    notes: etaItem?.notes || "--",
    addedAt: dayjs(etaItem.addedAt).format('DD MMM YYYY · HH:mm'),
  })) || [];

  const etaColumns = [
    { title: 'ETA', dataIndex: 'eta', key: 'eta' },
    { title: 'Notes', dataIndex: 'notes', key: 'notes' },
    { title: 'Added At', dataIndex: 'addedAt', key: 'addedAt' },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div style={styles.container}>
      
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>{taskData?.dutyDetail?.name || 'Untitled Task'}</h2>
          
          {!isLoading && (
            <Space size={6}>
              <StatusTag color={getPriorityColor(taskData?.priority)} label={taskData?.priority?.toUpperCase() || 'NORMAL'} />
              <StatusTag color={getStatusColor(taskData?.status)} label={taskData?.status?.toUpperCase() || 'ON-SCHEDULE'} />
              {isRecurring && <StatusTag color={{ bg: '#eef2ff', text: '#4f46e5' }} label="Untill further notice" />}
            </Space>
          )}
        </div>

        <div style={styles.headerRight}>
          {isLoading ? (
            <Spinner />
          ) : 
          // isOneOffTask ?  null :
          // (
          //   <ActionButton onClick={handelCancelTask} disabled={isLoading} variant="outlined">
          //     Cancel Task
          //   </ActionButton>
          // ) : 
          (
            <Space size={8}>
              {isTaskCancelled ? (
                <ActionButton disabled variant="outlined">Task Cancelled</ActionButton>
              ) : (
                <ActionButton onClick={()=>handelCancelTask(taskData?._id, taskData?.templateDetail?._id)} disabled={isLoading} variant="filled">
                  Cancel Service Task
                </ActionButton>
              )}
              {isRecurring? isTaskStopped ? (
                <ActionButton disabled variant="outlined">
                  Already Terminated{stoppedOnDate ? ` on ${dayjs(stoppedOnDate).format("DD/MM/YYYY")}` : ""}
                </ActionButton>
              ) : (
                <ActionButton onClick={handelStopTask} disabled={isLoading} variant="outlined">
                  Terminate Site Contract
                </ActionButton>
              ) : null}
            </Space>
          )}
        </div>
      </div>

      {/* MAIN CONTENT - 2 COLUMN LAYOUT */}
      <div style={styles.content}>
        
        {/* LEFT COLUMN */}
        <div style={styles.column}>
          
          {/* Basic Info */}
          <Section title="Basic Information">
            <CompactGrid>
              <InfoItem icon={<EnvironmentOutlined />} label="Site" value={taskData?.siteDetail?.sitename} sub={taskData?.siteDetail?.postcode} />
              <InfoItem icon={<UserOutlined />} label="Assigned To" value={getAssigneeName()} />
              <InfoItem icon={<CalendarOutlined />} label="Schedule" value={isRecurring ? 'Until further notice' : 'One-off'} />
            </CompactGrid>
          </Section>

          {/* Time Schedule */}
          <Section title="Time Schedule">
            <CompactGrid>
              <InfoItem icon={<ClockCircleOutlined />} label="Start Time" value={formatDateTime(taskData?.startTime)} />
              <InfoItem icon={<ClockCircleOutlined />} label="End Time" value={formatDateTime(taskData?.endTime)} />
            </CompactGrid>
          </Section>

          {/* Financial Info */}
          <Section title="Rates">
            <CompactGrid>
              <InfoItem icon={<DollarOutlined />} label="Pay Rate" value={taskData?.payRate ? `${taskData.payRate.toUpperCase()}` : '---'} />
              <InfoItem icon={<DollarOutlined />} label="Bill Rate" value={taskData?.billRate ? `${taskData.billRate.toUpperCase()}` : '---'} />
            </CompactGrid>
          </Section>

          {
            taskData?.isCancel?
            <Section title="Cancellation Reason/Comments">
              <div style={styles.notesBox}>
                <b>Reason</b> 
                <div>
                  {taskData?.reason}
                </div>
                <b>Comments</b>
                <div>
                  {taskData?.comments}
                </div>
              </div>
            </Section>
            :null
          }


        </div>

        {/* RIGHT COLUMN */}
        <div style={styles.column}>
          
          {/* Notes */}
          <Section title="Notes">
            <div style={styles.notesBox}>
              {taskData?.notes || 'No notes available'}
            </div>
          </Section>
          {
            taskData?.templateDetail?.isTerminated?
            <Section title="Termination Reason/Comments">
              <div style={styles.notesBox}>
                <b>Reason</b> 
                <div>
                  {taskData?.templateDetail?.reason}
                </div>
                <b>Comments</b>
                <div>
                  {taskData?.templateDetail?.comments}
                </div>
              </div>
            </Section>
            :null
          }

          {/* ETA History */}
          {etaTableData.length > 0 && (
            <Section title="ETA History">
              <Table
                columns={etaColumns}
                dataSource={etaTableData}
              />
            </Section>
          )}

          {/* Audit Trail */}
          <Section title={<span><SwapOutlined style={{ marginRight: 6 }} />Transfer History</span>}>
            {auditTrailLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><Spinner /></div>
            ) : auditTrail.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>No transfer history for this task</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                {auditTrail.map((row, i) => {
                  const prevName = row.previousEmployeeDetail?.firstname
                    ? `${row.previousEmployeeDetail.firstname} ${row.previousEmployeeDetail.lastname || ''}`.trim()
                    : row.previousSupplierDetail?.name || '---';
                  const newName = row.newEmployeeDetail?.firstname
                    ? `${row.newEmployeeDetail.firstname} ${row.newEmployeeDetail.lastname || ''}`.trim()
                    : row.newSupplierDetail?.name || '---';
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, position: 'relative' }}>
                      {/* Timeline spine */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: '#eff6ff', border: '2px solid #3b82f6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          zIndex: 1,
                        }}>
                          <SwapOutlined style={{ fontSize: 12, color: '#3b82f6' }} />
                        </div>
                        {i < auditTrail.length - 1 && (
                          <div style={{ width: 2, flex: 1, background: '#e5e7eb', minHeight: 16 }} />
                        )}
                      </div>

                      {/* Card */}
                      <div style={{
                        flex: 1, background: '#fff', border: '1px solid #e5e7eb',
                        borderRadius: 6, padding: '8px 10px', marginBottom: 10,
                      }}>
                        {/* Top row: action tag + date */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{row.action}</Tag>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>
                            {row.createdAt ? dayjs(row.createdAt).format('DD MMM YYYY · HH:mm') : '---'}
                          </span>
                        </div>

                        {/* Assignee transfer */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                          <span style={tlStyles.pill}>{prevName}</span>
                          <ArrowRightOutlined style={{ fontSize: 11, color: '#6b7280' }} />
                          <span style={{ ...tlStyles.pill, background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }}>{newName}</span>
                        </div>

                        {/* Meta row */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 4 }}>
                          {row.dispatchMethod && (
                            <span style={tlStyles.meta}><b>Method:</b> {row.dispatchMethod}</span>
                          )}
                          {row.transferReason && (
                            <span style={tlStyles.meta}><b>Reason:</b> {row.transferReason}</span>
                          )}
                          {row.createdByDetail?.email && (
                            <span style={tlStyles.meta}><b>By:</b> {row.createdByDetail.email}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const Section = ({ title, children }) => (
  <div style={styles.section}>
    <h3 style={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);

const CompactGrid = ({ children }) => (
  <div style={styles.compactGrid}>{children}</div>
);

const InfoItem = ({ icon, label, value, sub }) => (
  <div style={styles.infoItem}>
    <div style={styles.label}>
      <span style={styles.icon}>{icon}</span>
      {label}
    </div>
    <div style={styles.value}>{value || '---'}</div>
    {sub && <div style={styles.subValue}>{sub}</div>}
  </div>
);

const ActionButton = ({ children, onClick, disabled, variant = 'outlined' }) => {
  const buttonClass = disabled ? `disabled-btn drawer-${variant}-btn` : `drawer-${variant}-btn`;
  return (
    <button className={buttonClass} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

const StatusTag = ({ color, label }) => (
  <Tag style={{
    background: color.bg,
    color: color.text,
    border: 0,
    borderRadius: 4,
    padding: '2px 8px',
    fontWeight: 500,
    fontSize: 11,
    margin: 0,
  }}>
    {label}
  </Tag>
);

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  container: {
    background: '#ffffff',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },

  headerRight: {
    flexShrink: 0,
  },

  title: {
    fontSize: 18,
    fontWeight: 600,
    color: '#0f172a',
    margin: 0,
  },

  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    padding: 20,
    overflow: 'auto',
    flex: 1,
  },

  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
    overflow: 'hidden',
  },

  section: {
    background: '#f9fafb',
    borderRadius: 6,
    padding: 12,
    border: '1px solid #e5e7eb',
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 10px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  compactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },

  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },

  label: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: '#64748b',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },

  icon: {
    fontSize: 12,
    color: '#3b82f6',
  },

  value: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0f172a',
    lineHeight: 1.3,
  },

  subValue: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: 400,
  },

  notesBox: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    padding: 10,
    fontSize: 12,
    color: '#374151',
    lineHeight: 1.5,
    minHeight: 60,
    maxHeight: 120,
    overflow: 'auto',
  },

};

const tlStyles = {
  pill: {
    background: '#f3f4f6',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    padding: '2px 7px',
    fontSize: 12,
    fontWeight: 500,
  },
  meta: {
    fontSize: 11,
    color: '#6b7280',
  },
};

export default TaskDetailsView;